/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { useDatatip } from '#hooks/PRIVATE_useDatatip';
import { calculateOffset } from '#utils/PRIVATE_visLayoutUtils/layoutUtils';
import {
  DataCursorPosition,
  Info,
  ReferenceObjectData,
  Scale,
  ValueFormats
} from '#utils/UNSAFE_visTypes/chart';
import { BarItemData, BarSeriesData } from '#UNSAFE_BarChart/barChart.types';
import { Dimension } from '#utils/UNSAFE_visTypes/common';
import { getDefaultDatatipRenderer } from '#utils/PRIVATE_chartUtils/dataTipUtils';
import { LineAreaItem, LineAreaSeriesData } from '#UNSAFE_LineAreaChart/lineAreaChart.types';
import { getColorRamp } from '#utils/UNSAFE_visUtils';
import { Placement } from '#UNSAFE_Floating';
import { useUser } from '#hooks/UNSAFE_useUser';
import { ScatterItem } from '#UNSAFE_ScatterChart/scatterChart.types';
import { GroupLevelItem } from '#PRIVATE_Axis/groupAxis/hierarchicalUtils';
import { BundleType } from '#resources/nls/bundle';
import { DatatipConfig } from '#utils/UNSAFE_visTypes/common';
import { ChartDatatipDetail } from '#utils/UNSAFE_visTypes/chart/chart.types';

type Props<
  K extends string | number,
  D extends LineAreaItem<K> | ScatterItem<K> | BarItemData<K>
> = {
  series: (BarSeriesData<K, D> | LineAreaSeriesData<K, D>)[];
  leafGroupsInfo: GroupLevelItem[];
  isDataCursorEnabled?: boolean;
  dataCursorPosition?: DataCursorPosition;
  isHoriz: boolean;
  getDataItemPos: (seriesIndex: number, groupIndex: number) => Dimension | undefined;
  datatip?: DatatipConfig<ChartDatatipDetail<K, D>>;
  xScale: Scale;
  getDataItem: (seriesIndex: number, groupIndex: number, groupPath: number[]) => D | undefined;
  getRefItem: (id: string) => ReferenceObjectData | undefined;
  getRefObjectBounds: (id: string) => Dimension | undefined;
  getRefAxis: (id: string) => 'x' | 'y' | 'y2' | undefined;
  hoveredItemInfo?: Info;
  focusedItemInfo?: Info;
  width: number;
  height: number;
  valueFormats?: ValueFormats;
  findNearest: (x: number, y?: number, y2?: number) => any;
  timeAxisType?: 'mixedFrequency' | 'skipGaps' | 'enabled';
  touchResponse: 'touchStart' | 'touchHold';
  isDisabled: boolean;
  defaultXFormatter?: (value: number) => string;
  defaultYFormatter?: (value: number) => string;
  defaultY2Formatter?: (value: number) => string;
  hasXYValues?: boolean;
  getGroupFromPath: (path: number[]) => string[];
  translations: BundleType;
};
const DATA_CURSOR_OFFSET = 10;

export const useChartDatatip = <
  K extends string | number,
  D extends LineAreaItem<K> | ScatterItem<K> | BarItemData<K>
>({
  isDataCursorEnabled,
  dataCursorPosition,
  isHoriz,
  datatip,
  xScale,
  getDataItem,
  getRefItem,
  getDataItemPos,
  getRefObjectBounds,
  getRefAxis,
  hoveredItemInfo,
  focusedItemInfo,
  series,
  valueFormats,
  leafGroupsInfo,
  width,
  height,
  findNearest,
  timeAxisType,
  touchResponse,
  isDisabled,
  defaultXFormatter,
  defaultYFormatter,
  defaultY2Formatter,
  hasXYValues,
  translations,
  getGroupFromPath
}: Props<K, D>) => {
  const hasData = leafGroupsInfo.length > 0 && series.length > 0;
  let activeInfo = !hasData
    ? undefined
    : !isDisabled
    ? focusedItemInfo?.isCurrent
      ? focusedItemInfo
      : hoveredItemInfo?.isCurrent &&
        ((hoveredItemInfo.seriesIndex !== undefined && hoveredItemInfo.groupIndex !== undefined) ||
          hoveredItemInfo?.refObjId !== undefined)
      ? hoveredItemInfo
      : undefined
    : undefined;

  const anchor = focusedItemInfo?.isCurrent || isDataCursorEnabled ? 'element' : 'pointer';
  const elementBounds = focusedItemInfo?.isCurrent
    ? focusedItemInfo.refObjId != null
      ? getRefObjectBounds(focusedItemInfo.refObjId)
      : getDataItemPos(focusedItemInfo.seriesIndex!, focusedItemInfo.groupIndex!)
    : undefined;

  const { direction } = useUser();
  const isRtl = direction === 'rtl';

  let placement = 'top-start' as Placement;
  let offset = calculateOffset(isRtl, width, elementBounds);

  if (
    isDataCursorEnabled &&
    dataCursorPosition?.x != null &&
    (dataCursorPosition?.y != null || dataCursorPosition?.y2 != null)
  ) {
    const neareast = findNearest(dataCursorPosition.x, dataCursorPosition.y, dataCursorPosition.y2);
    activeInfo = activeInfo || neareast;
    // TOD0: The offset for data cursor should be calculated according to
    // datatip content dimensions as well.
    const xPos = xScale.transform(dataCursorPosition.x);
    offset = { mainAxis: -1, crossAxis: -1 };
    if (isHoriz) {
      placement = 'end';
      offset.mainAxis = -1 * DATA_CURSOR_OFFSET;
      offset.crossAxis = xPos - height / 2;
    } else {
      placement = 'top';
      offset.crossAxis = isRtl ? width / 2 - xPos : xPos - width / 2;
      offset.mainAxis = -1 * DATA_CURSOR_OFFSET;
    }
  }
  let dtContent;
  let dtBorderColor;
  let dtDefaultContainer;
  let item: D | ReferenceObjectData | undefined;
  if (activeInfo?.refObjId != undefined) {
    item = getRefItem(activeInfo?.refObjId);
    const axis = item ? getRefAxis(activeInfo.refObjId) : undefined;
    const datatipContext =
      item && axis
        ? item.type === 'line'
          ? { type: 'referenceLine' as const, axis, data: item }
          : { type: 'referenceArea' as const, axis, data: item }
        : undefined;
    if (datatipContext) {
      const customDatatip = datatip?.(datatipContext);
      dtContent = customDatatip?.content;
      dtBorderColor = customDatatip?.borderColor;
      dtDefaultContainer = customDatatip?.defaultContainer;
      if (!dtContent) {
        dtContent = datatipContext.data.accessibleLabel;
      }
    }
  } else if (activeInfo?.seriesIndex != null && activeInfo?.groupIndex != null) {
    item = getDataItem(
      activeInfo.seriesIndex,
      activeInfo.groupIndex!,
      leafGroupsInfo[activeInfo.groupIndex].path
    );
    const datatipContext = item
      ? {
          type: 'item' as const,
          data: item,
          seriesIndex: activeInfo.seriesIndex,
          groupIndex: activeInfo.groupIndex,
          id: item.id,
          groupPath: leafGroupsInfo[activeInfo.groupIndex].path
        }
      : undefined;

    if (datatipContext) {
      const customDatatip = datatip?.(datatipContext);
      dtContent = customDatatip?.content;
      dtBorderColor = customDatatip?.borderColor;
      dtDefaultContainer = customDatatip?.defaultContainer;
      if (dtContent !== '') {
        const defaultDatatip = getDefaultDatatipRenderer({
          series: series[activeInfo.seriesIndex],
          groups: getGroupFromPath(leafGroupsInfo[activeInfo.groupIndex].path),
          translations,
          valueFormats,
          timeAxisType,
          defaultXFormatter,
          defaultYFormatter,
          defaultY2Formatter,
          hasXYValues
        })(datatipContext);
        dtContent = dtContent || defaultDatatip.content;
        dtBorderColor = dtBorderColor || defaultDatatip.borderColor;
        dtDefaultContainer = dtDefaultContainer || defaultDatatip.defaultContainer;
      }
    }
  }
  const colors = getColorRamp();
  const borderColor =
    dtBorderColor ||
    (activeInfo &&
      ((series[activeInfo.seriesIndex!] as BarSeriesData<K, D>)?.color ||
        (series[activeInfo.seriesIndex!] as LineAreaSeriesData<K, D>)?.lineColor ||
        (series[activeInfo.seriesIndex!] as LineAreaSeriesData<K, D>)?.areaColor)) ||
    (activeInfo && colors[activeInfo.seriesIndex! % colors.length]);

  const { datatipContent, datatipProps } = useDatatip({
    content: dtContent,
    anchor,
    placement,
    defaultContainer: dtDefaultContainer,
    offset,
    borderColor,
    touchResponse,
    isDisabled
  });

  return { datatipContent, datatipProps };
};
